import type { ReactNode } from "react";
import React, { createContext, useCallback, useContext, useMemo } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";

import ApiHttp from "../ApiHttp";
import { useLibrary } from "../dbbl/providers/useLibrary.hook";

import { Role } from "./Role";

type RolesContextType = {
  isLoading: boolean;
  roles: Role[];
  assignableRoles: Role[];
  error?: Error;
  refresh: () => void;
  createRole: (role: ReturnType<Role["serialize"]>) => Promise<Role>;
  updateRole: (
    role: ReturnType<Role["serialize"]> & { uuid: string },
  ) => Promise<Role>;
  deleteRole: (uuid: string) => Promise<void>;
};

const rolesContext = createContext<RolesContextType>({
  isLoading: false,
  roles: [],
  assignableRoles: [],
  error: undefined,
  refresh: () => {},
  createRole: () => Promise.resolve(new Role({ name: "" })),
  updateRole: () => Promise.resolve(new Role({ name: "", uuid: "required" })),
  deleteRole: () => Promise.resolve(),
});

export const useRolesContext = () => useContext(rolesContext);

export const RolesProvider = ({
  orgUuid,
  children,
}: {
  // Nullable so consumers can mount the provider unconditionally: swapping it
  // in and out as an org appears remounts everything below it.
  orgUuid: UUID | Brand<string, "OrganizationId"> | null | undefined;
  children: ReactNode;
}) => {
  const [lastUpdateRequested, setLastUpdateRequested] = React.useState<Date>(
    new Date(),
  );
  const { token, apiUrl } = useLibrary("network");
  const { isLoading, data, error } = useQuery({
    queryKey: ["roles", orgUuid, lastUpdateRequested.getTime(), token, apiUrl],
    queryFn: () =>
      ApiHttp.fetch<{ roles: API.Role[] }>(
        `${apiUrl}/organizations/${orgUuid}/roles`,
        {
          token,
          endpointIsUrl: true,
        },
      ).then((responseData) =>
        responseData.roles.map((r: API.Role) => Role.deserialize(r)),
      ),
    enabled: !!orgUuid && !!token,
  });
  const refresh = useCallback(() => setLastUpdateRequested(new Date()), []);
  const createMutation = useMutation({
    mutationFn: (role: ReturnType<Role["serialize"]>) =>
      ApiHttp.fetch<API.Role>(
        `${apiUrl}/organizations/${orgUuid}/roles`,
        { method: "POST", token, endpointIsUrl: true },
        role,
      ).then((responseData) => Role.deserialize(responseData)),
    onSuccess: refresh,
  });
  const updateMutation = useMutation({
    mutationFn: (role: ReturnType<Role["serialize"]> & { uuid: string }) =>
      ApiHttp.fetch<API.Role>(
        `${apiUrl}/organizations/${orgUuid}/roles/${role.uuid}`,
        { method: "PATCH", token, endpointIsUrl: true },
        role,
      ).then((responseData) => Role.deserialize(responseData)),
    onSuccess: refresh,
  });
  const deleteMutation = useMutation({
    mutationFn: (uuid: string) =>
      ApiHttp.fetch<void>(`${apiUrl}/organizations/${orgUuid}/roles/${uuid}`, {
        method: "DELETE",
        token,
        endpointIsUrl: true,
      }),
    onSuccess: refresh,
  });
  const value = useMemo(() => {
    const allRoles = data || [];
    return {
      isLoading,
      roles: allRoles,
      assignableRoles: allRoles.filter((role) => role.isAssignable()),
      error: error || undefined,
      refresh,
      createRole: createMutation.mutateAsync,
      updateRole: updateMutation.mutateAsync,
      deleteRole: deleteMutation.mutateAsync,
    };
  }, [
    isLoading,
    data,
    error,
    refresh,
    createMutation.mutateAsync,
    updateMutation.mutateAsync,
    deleteMutation.mutateAsync,
  ]);
  return (
    <rolesContext.Provider value={value}>{children}</rolesContext.Provider>
  );
};
